/**
 * Shared-DD row ↔ domain mappers.
 *
 * Pure functions converting snake_case Postgres rows (shared_dd, dd_collaborators,
 * dd_notes, dd_note_reactions) into the camelCase domain types the UI consumes.
 * Kept separate from the hooks so they can be unit-tested without Supabase.
 */

import type {
  SharedDueDiligence,
  DDCollaborator,
  DDNote,
  NoteReaction,
  DDConclusion,
  CollaboratorRole,
  CollaboratorStatus,
  NoteCategory,
  ConclusionDecision,
} from './types';

// ── DB row shapes (the columns we select) ──────────────────────────────────
export interface SharedDDRow {
  id: string;
  startup_id: string;
  startup_name: string;
  owner_id: string;
  owner_name: string;
  status: 'active' | 'closed';
  syndicate_mode: boolean;
  created_at: string;
  updated_at: string;
}

export interface CollaboratorRow {
  id: string;
  shared_dd_id: string;
  investor_user_id: string;
  investor_name: string;
  investor_email: string;
  role: CollaboratorRole;
  status: CollaboratorStatus;
  conclusion_decision: ConclusionDecision | null;
  conclusion_amount: number | null;
  conclusion_notes: string | null;
  conclusion_updated_at: string | null;
  invited_at: string;
  joined_at: string | null;
  last_active_at: string | null;
  created_at: string;
}

export interface NoteRow {
  id: string;
  shared_dd_id: string;
  author_id: string;
  author_name: string;
  content: string;
  category: NoteCategory;
  is_private: boolean;
  document_ref_id: string | null;
  document_ref_name: string | null;
  document_ref_type: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ReactionRow {
  id: string;
  note_id: string;
  investor_user_id: string;
  reaction_type: NoteReaction['type'];
  created_at: string;
}

// ── Mappers ─────────────────────────────────────────────────────────────────

export function conclusionFromRow(row: CollaboratorRow): DDConclusion | undefined {
  if (!row.conclusion_decision) return undefined;
  return {
    investorId: row.investor_user_id,
    decision: row.conclusion_decision,
    investmentAmount: row.conclusion_amount ?? undefined,
    notes: row.conclusion_notes ?? undefined,
    updatedAt: row.conclusion_updated_at ?? row.created_at,
  };
}

export function collaboratorFromRow(row: CollaboratorRow): DDCollaborator {
  return {
    id: row.id,
    odId: row.shared_dd_id, // domain field name is `odId` (= shared DD id)
    investorId: row.investor_user_id,
    investorName: row.investor_name,
    investorEmail: row.investor_email,
    role: row.role,
    status: row.status,
    invitedAt: row.invited_at,
    joinedAt: row.joined_at ?? undefined,
    lastActiveAt: row.last_active_at ?? undefined,
    conclusion: conclusionFromRow(row),
  };
}

export function reactionFromRow(row: ReactionRow): NoteReaction {
  return { investorId: row.investor_user_id, type: row.reaction_type };
}

export function noteFromRow(row: NoteRow, reactions: ReactionRow[] = []): DDNote {
  return {
    id: row.id,
    sharedDdId: row.shared_dd_id,
    authorId: row.author_id,
    authorName: row.author_name,
    content: row.content,
    category: row.category,
    createdAt: row.created_at,
    updatedAt: row.updated_at ?? undefined,
    isPrivate: row.is_private,
    reactions: reactions.length ? reactions.map(reactionFromRow) : undefined,
    documentRef: row.document_ref_id
      ? {
          documentId: row.document_ref_id,
          documentName: row.document_ref_name ?? '',
          documentType: row.document_ref_type ?? undefined,
        }
      : undefined,
  };
}

/**
 * Founder-facing view of a session: a founder may read WHO is running DD on their
 * startup (the lead investor on the session row) but NOT the investors' private
 * notes / conclusions (RLS denies those). So surface the owner as the single
 * visible collaborator and leave notes empty.
 */
export function founderSessionFromRow(session: SharedDDRow): SharedDueDiligence {
  return {
    id: session.id,
    startupId: session.startup_id,
    startupName: session.startup_name,
    ownerId: session.owner_id,
    ownerName: session.owner_name,
    createdAt: session.created_at,
    updatedAt: session.updated_at,
    status: session.status,
    syndicateMode: session.syndicate_mode,
    collaborators: [
      {
        id: `owner-${session.id}`,
        odId: session.id,
        investorId: session.owner_id,
        investorName: session.owner_name,
        investorEmail: '',
        role: 'owner',
        status: 'active',
        invitedAt: session.created_at,
        joinedAt: session.created_at,
      },
    ],
    notes: [],
  };
}

/** Assemble a full session from its rows (collaborators + notes + reactions-by-note). */
export function sharedDDFromRows(
  session: SharedDDRow,
  collaborators: CollaboratorRow[],
  notes: NoteRow[],
  reactionsByNote: Map<string, ReactionRow[]>,
): SharedDueDiligence {
  return {
    id: session.id,
    startupId: session.startup_id,
    startupName: session.startup_name,
    ownerId: session.owner_id,
    ownerName: session.owner_name,
    createdAt: session.created_at,
    updatedAt: session.updated_at,
    status: session.status,
    syndicateMode: session.syndicate_mode,
    collaborators: collaborators.map(collaboratorFromRow),
    notes: notes.map((n) => noteFromRow(n, reactionsByNote.get(n.id) ?? [])),
  };
}
